import React, {
  lazy,
  Suspense,
  useState,
  useCallback,
  FunctionComponent,
} from 'react';
import moment from 'moment';
import { forNamespace } from 'app/common/lib/util/i18n';
import { SessionStorage } from '@trello/storage';
import Banner from 'app/common/components/banner/banner';
import {
  Button,
  PrimaryButton,
} from 'app/common/components/controls/button/button';
import styles from './AtlassianAccountOnboardingBanner.less';
import { Noun, Verb } from '@trello/analytics';
import { track } from './Analytics';
import { AtlassianAccountOnboardingLinks } from './AtlassianAccountOnboardingLinks';
import { Sky25 } from '@trello/colors';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { useAtlassianAccountOnboardingQuery } from '@trello/graphql/generated';

const AtlassianAccountOnboardingOverlayPromise = import(/* webpackChunkName: "gamma-atlassian-account-onboarding" */ './AtlassianAccountOnboardingOverlay');
const AtlassianAccountOnboardingOverlay = lazy(
  () => AtlassianAccountOnboardingOverlayPromise,
);

import { ScreenType } from './AtlassianAccountOnboardingOverlay';

//Preload images for overlays here
const IMAGE_MAP: Map<ScreenType, HTMLImageElement> = new Map();
const OVERLAY_IMAGES = [
  {
    type: ScreenType.ACCOUNT_TYPE,
    src: require('resources/images/onboarding-banner/one-thing.png'),
  },
  {
    type: ScreenType.LOGIN_WITH_ATLASSIAN,
    src: require('resources/images/onboarding-banner/priming-for-aa.png'),
  },
  {
    type: ScreenType.NOT_WORK,
    src: require('resources/images/onboarding-banner/one-more-step.png'),
  },
  {
    type: ScreenType.REMOVE_EMAILS,
    src: require('resources/images/onboarding-banner/removing-emails.png'),
  },
  {
    type: ScreenType.SUCCESS,
    src: require('resources/images/onboarding-banner/atlassian-setup-complete.png'),
  },
  {
    type: ScreenType.WORK_AND_OTHER,
    src: require('resources/images/onboarding-banner/one-thing.png'),
  },
];

let analyticsContext = {};
const analyticsSource = 'atlassianAccountOnboardingBanner';
const SNOOZED_STORAGE_KEY = 'aa-banner-snoozed';
const format = forNamespace(['aa onboarding', 'banner']);

const isSnoozed = () => {
  const snoozed = SessionStorage.get(SNOOZED_STORAGE_KEY);
  return snoozed && moment().isBefore(snoozed.expires);
};

export const AtlassianAccountOnboardingBanner: FunctionComponent = () => {
  const [isOverlayOpen, setOverlayOpen] = useState(false);
  const [screen, setScreen] = useState(ScreenType.REMOVE_EMAILS);
  const [snoozed, setSnoozed] = useState(isSnoozed());
  const [snoozedCallback, setSnoozedCallback] = useState(false);

  const { data } = useAtlassianAccountOnboardingQuery({
    variables: { memberId: 'me' },
  });

  const snooze = useCallback(() => {
    SessionStorage.set(SNOOZED_STORAGE_KEY, {
      expires: moment()
        .add(1, 'days')
        .hour(4)
        .startOf('hour'),
    });
    setSnoozed(true);
    track({
      verb: Verb.HIDES,
      noun: Noun.BANNER,
      method: 'by clicking dismiss button',
      context: analyticsContext,
      actionSubject: 'button',
      actionSubjectId: 'snoozeButton',
      action: 'clicks',
      source: analyticsSource,
    });
  }, []);

  const onClickWork = useCallback(() => {
    setScreen(ScreenType.REMOVE_EMAILS);
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.ONBOARDING_MENU,
      method: 'by clicking work button',
      context: analyticsContext,
      source: analyticsSource,
      action: 'clicks',
      actionSubject: 'button',
      actionSubjectId: 'workButton',
    });
  }, []);

  const onClickNotWork = useCallback(() => {
    setScreen(ScreenType.ACCOUNT_TYPE);
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.ONBOARDING_MENU,
      method: 'by clicking not work button',
      context: analyticsContext,
      source: analyticsSource,
      action: 'clicks',
      actionSubject: 'button',
      actionSubjectId: 'notWorkButton',
    });
  }, []);

  const closeOverlayImplicitly = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.DISMISSES,
      noun: Noun.ONBOARDING_MENU,
      method: 'with an implicit input',
      context: analyticsContext,
      source: analyticsSource,
      action: 'closed',
      actionSubject: 'overlay',
      actionSubjectId: 'atlassianAccountOnboardingOverlay',
    });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
  }, []);

  const me = data && data.member;
  const name = me && me.fullName;
  const aaEmail = me && me.aaEmail;
  const aaId = me && me.aaId;
  const aaLogin = me && me.logins.find(({ email }) => email === aaEmail);

  const ent = me && me.enterprises[0];
  const orgName = ent && ent.displayName;

  // Ensure we have all the data necessary to render (none of our fields are null)
  if (!me || !name || !ent || !orgName || !aaEmail || !aaId || !aaLogin) {
    return null;
  }

  analyticsContext = {
    enterpriseId: me.enterprises[0].id,
    emailCount: me.logins.length,
  };

  // If we are snoozed check the time the snooze expires and
  // reset the state once we hit expiration
  if (snoozed) {
    if (!snoozedCallback) {
      const storedSnooze = SessionStorage.get(SNOOZED_STORAGE_KEY);
      const timeDifference = moment(storedSnooze.expires).diff(moment());

      setTimeout(() => {
        SessionStorage.unset(SNOOZED_STORAGE_KEY);
        setSnoozed(false);
        setSnoozedCallback(false);
      }, timeDifference);

      setSnoozedCallback(true);
    }

    return null;
  }

  // We only want to show the banner to members who are not already aa mastered,
  // and belong to an enterprise with accessEnabled
  const shouldDisplay = !me.isAaMastered && ent.accessEnabled;
  if (!shouldDisplay) {
    return null;
  }

  // We made it to render the banner without
  // any pictures being preloaded
  if (IMAGE_MAP.size === 0) {
    OVERLAY_IMAGES.forEach((image) => {
      const loadedImage = new Image();
      loadedImage.src = image.src;
      loadedImage.className = styles.onboardingImage;
      IMAGE_MAP.set(image.type, loadedImage);
    });
  }

  return (
    <div>
      <Banner
        onDismiss={snooze}
        infoUrl={AtlassianAccountOnboardingLinks.Help}
        bannerColor={Sky25}
        className={styles.banner}
        shadow
        roundedCorners
        alignCloseTop
      >
        <div className={styles.container}>
          <div className={styles.body}>
            <p className={styles.text}>
              {format('claimed', {
                name,
                orgName,
              })}
            </p>
          </div>
          <div className={styles.controls}>
            <PrimaryButton onClick={onClickWork}>
              {format('only work', { orgName })}
            </PrimaryButton>
            <Button onClick={onClickNotWork}>
              {format('not only work', { orgName })}
            </Button>
            {isOverlayOpen && (
              <Overlay
                background={OverlayBackground.LIGHT}
                onClose={closeOverlayImplicitly}
                alignment={OverlayAlignment.TOP}
              >
                <Suspense fallback={null}>
                  <AtlassianAccountOnboardingOverlay
                    aaEmail={aaEmail || ''}
                    logins={(me && me.logins) || []}
                    orgName={orgName || ''}
                    dismiss={closeOverlay}
                    screen={screen}
                    setScreen={setScreen}
                    analyticsContext={analyticsContext}
                    images={IMAGE_MAP}
                  />
                </Suspense>
              </Overlay>
            )}
          </div>
        </div>
      </Banner>
    </div>
  );
};
